import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ExternalLink, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../api";

const Chips = ({ faltas }) => (
  <span className="inline-flex flex-wrap gap-1" data-testid="faltas-chips">
    {faltas.map((f) => (
      <span
        key={f.campo}
        className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${
          f.bloqueante
            ? "bg-rose-100 border-rose-300 text-rose-700"
            : "bg-amber-50 border-amber-300 text-amber-800"
        }`}
      >
        {f.label}
      </span>
    ))}
  </span>
);

/** Aviso na ficha do curso: lista TODOS os campos obrigatórios em falta. */
export function EntidadeFormadoraAviso({ cursoId }) {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!cursoId) { setInfo(null); return; }
    let vivo = true;
    adminApi.cursoChecklist(cursoId)
      .then((r) => vivo && setInfo(r))
      .catch(() => vivo && setInfo(null));
    return () => { vivo = false; };
  }, [cursoId]);

  if (!info || info.completo) return null;
  const bloqueado = info.bloqueado;
  return (
    <div
      className={`mb-4 rounded-md border-2 p-4 flex items-start gap-3 ${
        bloqueado ? "border-rose-500 bg-rose-50" : "border-amber-400 bg-amber-50"
      }`}
      data-testid={bloqueado ? "aviso-sem-entidade-formadora" : "aviso-campos-em-falta"}
    >
      <AlertTriangle size={20} className={`shrink-0 mt-0.5 ${bloqueado ? "text-rose-600" : "text-amber-600"}`} />
      <div className="text-sm">
        <p className={`font-bold ${bloqueado ? "text-rose-700" : "text-amber-800"}`}>
          {bloqueado
            ? "Entidade Formadora em falta — comunicações suspensas"
            : `${info.faltas.length} campo(s) por preencher`}
        </p>
        <p className={`mt-0.5 ${bloqueado ? "text-rose-700/90" : "text-amber-800/90"}`}>
          {bloqueado
            ? "Enquanto a Entidade Formadora estiver vazia este curso é ignorado: não são enviados links de inscrição, lembretes, emails de avaliação nem diplomas aos clientes."
            : "Estes campos não travam os envios, mas saem vazios nos diplomas, certificados e no DTP."}
        </p>
        <div className="mt-2">
          <span className="text-[11px] uppercase tracking-wider text-[#978D78] font-bold mr-2">
            Em falta
          </span>
          <Chips faltas={info.faltas} />
        </div>
      </div>
    </div>
  );
}

/** Preenchimento rápido: define a Entidade Formadora em vários cursos de uma vez. */
export function EntidadeFormadoraBulk({ onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cursos, setCursos] = useState([]);
  const [sugestoes, setSugestoes] = useState([]);
  const [bloqueados, setBloqueados] = useState(0);
  const [sel, setSel] = useState([]);
  const [entidade, setEntidade] = useState("");
  const [localEntidade, setLocalEntidade] = useState("");
  const [apenasFuturos, setApenasFuturos] = useState(true);
  const [soBloqueados, setSoBloqueados] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.cursosEntidadeFormadoraPendentes(apenasFuturos);
      setCursos(r.cursos || []);
      setSugestoes(r.sugestoes || []);
      setBloqueados(r.bloqueados || 0);
      setSel([]);
      setEntidade((v) => v || (r.sugestoes || [])[0] || "");
    } catch (e) { toast.error(e.message || "Erro a carregar cursos pendentes"); }
    finally { setLoading(false); }
  }, [apenasFuturos]);

  useEffect(() => { load(); }, [load]);

  const visiveis = soBloqueados ? cursos.filter((c) => c.bloqueado) : cursos;
  const toggle = (id) =>
    setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const todos = () =>
    setSel((s) => (s.length === visiveis.length ? [] : visiveis.map((c) => c.ID_Curso)));

  const aplicar = async () => {
    if (!sel.length || !entidade.trim()) return;
    if (!window.confirm(
      `Definir a Entidade Formadora "${entidade}" em ${sel.length} curso(s)? ` +
      "A partir daí voltam a ser enviadas as comunicações automáticas destes cursos."
    )) return;
    setSaving(true);
    try {
      const r = await adminApi.cursosEntidadeFormadoraBulk({
        ids: sel, entidade: entidade.trim(), local_entidade: localEntidade.trim() || undefined,
      });
      toast.success(`${r.atualizados} curso(s) actualizado(s).`);
      await load();
      onSaved?.();
    } catch (e) { toast.error(e.message || "Erro a aplicar"); }
    finally { setSaving(false); }
  };

  return (
    <section className="mb-6 p-4 bg-white border border-[#E6E1D6] rounded-md" data-testid="entidade-formadora-bulk">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-bold text-[#0F1829] flex items-center gap-2">
            <Wand2 size={14} /> Campos em falta &amp; preenchimento rápido
          </h2>
          <p className="text-xs text-[#6B7280] mt-0.5">
            {bloqueados > 0
              ? `${bloqueados} curso(s) com comunicações suspensas por falta de Entidade Formadora.`
              : "Nenhum curso com comunicações suspensas."}
            {" "}Selecione e preencha a Entidade Formadora de vários de uma vez.
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <label className="inline-flex items-center gap-2 text-xs text-[#6E6754]">
            <input type="checkbox" checked={apenasFuturos}
              onChange={(e) => setApenasFuturos(e.target.checked)} data-testid="ef-apenas-futuros" />
            Só cursos a decorrer ou futuros
          </label>
          <label className="inline-flex items-center gap-2 text-xs text-[#6E6754]">
            <input type="checkbox" checked={soBloqueados}
              onChange={(e) => setSoBloqueados(e.target.checked)} data-testid="ef-so-bloqueados" />
            Só os que têm comunicações suspensas
          </label>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mt-3">
        <div className="min-w-[240px]">
          <label className="block text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold mb-1">
            Entidade Formadora
          </label>
          <input list="ef-sugestoes" value={entidade} onChange={(e) => setEntidade(e.target.value)}
            placeholder="Ex.: Solfut, Lda" data-testid="ef-valor"
            className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm" />
          <datalist id="ef-sugestoes">
            {sugestoes.map((s) => <option key={s} value={s} />)}
          </datalist>
        </div>
        <div className="min-w-[200px]">
          <label className="block text-[11px] uppercase tracking-[0.18em] text-[#978D78] font-bold mb-1">
            Local da Entidade (opcional)
          </label>
          <input value={localEntidade} onChange={(e) => setLocalEntidade(e.target.value)}
            placeholder="deixe vazio para não alterar" data-testid="ef-local"
            className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm" />
        </div>
        <button onClick={aplicar} disabled={saving || !sel.length || !entidade.trim()}
          data-testid="ef-aplicar"
          className="px-4 py-2 rounded bg-[#0F1829] text-white text-xs font-semibold disabled:opacity-40 inline-flex items-center gap-2">
          {saving && <Loader2 size={13} className="animate-spin" />}
          Aplicar a {sel.length} curso(s)
        </button>
      </div>

      {loading ? (
        <p className="text-xs text-[#6B7280] mt-4 inline-flex items-center gap-2">
          <Loader2 size={13} className="animate-spin" /> A carregar…
        </p>
      ) : visiveis.length === 0 ? (
        <p className="text-xs text-emerald-700 mt-4" data-testid="ef-sem-pendentes">
          Não há cursos com campos obrigatórios em falta.
        </p>
      ) : (
        <table className="w-full text-xs mt-4" data-testid="ef-tabela">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#978D78]">
              <th className="py-1 w-8">
                <input type="checkbox" checked={sel.length === visiveis.length && visiveis.length > 0}
                  onChange={todos} data-testid="ef-selecionar-todos" />
              </th>
              <th>Curso</th><th>Início</th><th>Inscritos</th><th>Campos em falta</th><th className="w-24">Ficha</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((c) => (
              <tr key={c.ID_Curso} className={`border-t border-[#F0EDE4] ${c.bloqueado ? "bg-rose-50/40" : ""}`}>
                <td className="py-1.5 align-top">
                  <input type="checkbox" checked={sel.includes(c.ID_Curso)}
                    onChange={() => toggle(c.ID_Curso)} data-testid={`ef-check-${c.ID_Curso}`} />
                </td>
                <td className="align-top font-medium text-[#0F1829]">
                  <Link
                    to={`/admin/cursos/edit?curso=${c.ID_Curso}`}
                    data-testid={`ef-link-${c.ID_Curso}`}
                    className="hover:underline decoration-dotted"
                    title="Abrir a ficha deste curso"
                  >
                    {c.Nome} <span className="text-[#978D78]">#{c.ID_Curso}</span>
                  </Link>
                  {c.bloqueado && (
                    <span className="ml-2 px-1.5 py-0.5 rounded bg-rose-600 text-white text-[9px] uppercase tracking-wider">
                      envios suspensos
                    </span>
                  )}
                </td>
                <td className="align-top">{(c.DataInicio || "").slice(0, 10) || "—"}</td>
                <td className="align-top">{c.Inscritos ?? 0}</td>
                <td className="align-top py-1.5"><Chips faltas={c.faltas} /></td>
                <td className="align-top py-1.5">
                  <Link
                    to={`/admin/cursos/edit?curso=${c.ID_Curso}`}
                    data-testid={`ef-abrir-${c.ID_Curso}`}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#E6E1D6] text-[10px] font-semibold text-[#0F1829] hover:bg-[#F7F5EF]"
                  >
                    <ExternalLink size={11} /> Corrigir
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
